import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useNavigation } from 'expo-router'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { StepOption, StepOptions } from '@order/steps/types'
import { StepHrefFn } from '@order/steps/useStepHref'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import { useOptionColors } from 'hooks/useColor'

import OptionColor from './Option/OptionColor'
import OptionDefault from '../Default/Option/OptionDefault'

interface Props {
  title?: string
  desc?: string
  options: StepOptions
  href: StepHrefFn
}

interface BaseProps {
  title?: string
  desc?: string
  colors: ReturnType<typeof useOptionColors>
}

export const ColorsTemplateBase = ({ title, desc, colors }: BaseProps) => {
  const { t } = useTranslation(['ui'])

  return (
    <Page stage="configuration">
      <Header title={title} desc={desc} />
      <View className="flex flex-row flex-wrap gap-8 px-8 pb-8">
        {colors.map((option) => (
          <OptionColor color={option.color} key={option.id} option={option} />
        ))}
      </View>
    </Page>
  )
}

export default function ColorsTemplate({ title, desc, options, href }: Props) {
  const { t } = useTranslation(['ui'])
  const hasOptions = options.length > 1
  const [option, setOption] = useState<StepOption | undefined>(
    options.length > 1 ? undefined : options[0]
  )
  const colors = useOptionColors(option, href)

  const navigation = useNavigation()

  useEffect(() => {
    const remove = navigation.addListener('beforeRemove', (e) => {
      if (!hasOptions || !option) {
        return
      }
      // Prevent default behavior of leaving the screen
      if (e.data.action.type === 'GO_BACK') {
        e.preventDefault()
        setOption(undefined)
      }
    })
    return remove
  }, [hasOptions, option, navigation])

  return (
    <>
      <Page stage="configuration">
        <Header title={title} desc={desc} />
        <View className="flex flex-row flex-wrap gap-8 px-8 pb-8">
          {!!option &&
            colors.map((option) => (
              <OptionColor
                color={option.color}
                key={option.id}
                option={option}
              />
            ))}
          {!option &&
            options.map((option) => (
              <OptionDefault
                key={option.id}
                options={options.length}
                {...option}
                onPress={() => setOption(option)}
              />
            ))}
        </View>
        <SpaceButtons />
      </Page>
      {!!option && options.length > 1 && (
        <ButtonsFixed
          buttons={[
            {
              variant: 'outlined',
              label: t('ui:change'),
              iconStart: 'arrow-narrow-left',
              onPress: () => setOption(undefined),
            },
          ]}
        />
      )}
    </>
  )
}
